<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-text class="px-4 py-3">
              <v-row align="center">
                <v-col cols="12" md="6" class="d-flex align-center">
                  <v-btn icon small class="mr-2" @click="$router.push('/prescriptions')">
                    <v-icon>mdi-arrow-left</v-icon>
                  </v-btn>
                  <div>
                    <h2 class="text-h6 font-weight-bold primary--text mb-0">
                      Prescription #{{ prescription ? prescription.id : "" }}
                    </h2>
                    <p class="text-caption grey--text ma-0 mt-1">Prescription details</p>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="d-flex align-center justify-end flex-wrap">
                    <v-chip
                      v-if="prescription"
                      small
                      class="mr-2 mb-1"
                      :color="prescription.is_active ? 'success' : 'error'"
                      dark
                    >
                      <v-icon left x-small>{{ prescription.is_active ? "mdi-check-circle" : "mdi-close-circle" }}</v-icon>
                      {{ prescription.is_active ? "Active" : "Inactive" }}
                    </v-chip>
                    <v-btn small color="indigo" dark class="mr-2 mb-1" :loading="isDownloading" @click="downloadPdf">
                      <v-icon left small>mdi-file-pdf-box</v-icon>
                      Download PDF
                    </v-btn>
                    <v-btn small color="primary" class="mr-2 mb-1" :to="`/prescriptions/${$route.params.id}`">
                      <v-icon left small>mdi-pencil</v-icon>
                      Edit
                    </v-btn>
                    <v-btn v-if="canDeletePrescription" small color="error" class="mb-1" @click="showDeleteDialog = true">
                      <v-icon left small>mdi-delete</v-icon>
                      Delete
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="isLoading">
        <v-col cols="12" class="d-flex align-center justify-center" style="height: 300px">
          <health-loader />
        </v-col>
      </v-row>

      <template v-else-if="prescription">
        <v-row>
          <!-- Prescription Information -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 h-100">
              <div class="d-flex align-center mb-3">
                <v-icon color="primary" class="mr-2">mdi-prescription</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Prescription Information</span>
              </div>
              <div class="info-row"><span class="info-label">Prescription ID</span><span class="info-value">#{{ prescription.id }}</span></div>
              <div class="info-row">
                <span class="info-label">Status</span>
                <status-chip x-small :color="prescription.is_active ? 'success' : 'error'" :label="String(prescription.is_active ? 'Active' : 'Inactive')" />
              </div>
              <div class="info-row"><span class="info-label">Date &amp; Time</span><span class="info-value">{{ formatDateTime(prescription.created_at) }}</span></div>
              <div class="info-row">
                <span class="info-label">Source</span>
                <v-chip x-small :color="prescriptionSource.color" dark>
                  <v-icon x-small left>{{ prescriptionSource.icon }}</v-icon>
                  {{ prescriptionSource.label }}
                </v-chip>
              </div>
              <div class="info-row">
                <span class="info-label">Consultation ID</span>
                <span class="info-value">{{ prescription.consultation_id ? "#" + prescription.consultation_id : "N/A" }}</span>
              </div>
              <div v-if="prescription.consultation_id" class="info-row">
                <span class="info-label">Consultation No.</span>
                <span class="info-value">{{ consultationNo || "—" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Appointment ID</span>
                <span class="info-value">{{ appointmentId ? "#" + appointmentId : "N/A" }}</span>
              </div>
              <div v-if="appointmentId" class="info-row">
                <span class="info-label">Appointment No.</span>
                <span class="info-value">{{ appointmentOrderNo || "—" }}</span>
              </div>
            </v-card>
          </v-col>

          <!-- Doctor Information -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 h-100">
              <div class="d-flex align-center mb-3">
                <v-icon color="blue" class="mr-2">mdi-doctor</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Doctor Information</span>
              </div>
              <div class="info-row"><span class="info-label">Name</span><span class="info-value">{{ (prescription.doctor && prescription.doctor.user_name) || "—" }}</span></div>
              <div class="info-row"><span class="info-label">Category</span><span class="info-value">{{ (prescription.doctor && prescription.doctor.pathy && prescription.doctor.pathy.description) || "—" }}</span></div>
              <div class="info-row"><span class="info-label">Specialty</span><span class="info-value">{{ doctorSpecialties || "—" }}</span></div>
              <div class="info-row"><span class="info-label">Registration Number</span><span class="info-value">{{ doctorRegistrationNumber || "—" }}</span></div>
            </v-card>
          </v-col>

          <!-- Patient Information -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 h-100">
              <div class="d-flex align-center mb-3">
                <v-icon color="green" class="mr-2">mdi-account-heart</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Patient Information</span>
              </div>
              <div class="info-row"><span class="info-label">Name</span><span class="info-value">{{ (prescription.patient && prescription.patient.user_name) || "—" }}</span></div>
              <div class="info-row"><span class="info-label">Age</span><span class="info-value">{{ patientAge !== null ? patientAge + " yrs" : "—" }}</span></div>
              <div class="info-row"><span class="info-label">Gender</span><span class="info-value">{{ (prescription.patient && prescription.patient.gender && prescription.patient.gender.description) || "—" }}</span></div>
            </v-card>
          </v-col>

          <!-- Follow-up -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 h-100">
              <div class="d-flex align-center mb-3">
                <v-icon color="deep-orange" class="mr-2">mdi-calendar-clock</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Follow-up</span>
              </div>
              <div class="info-row">
                <span class="info-label">Follow-up Date</span>
                <span class="info-value">{{ prescription.follow_up_date ? formatDate(prescription.follow_up_date) : "Not scheduled" }}</span>
              </div>
            </v-card>
          </v-col>

          <!-- Clinical Notes -->
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-icon color="red darken-1" class="mr-2">mdi-clipboard-pulse</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Clinical Notes</span>
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">CHIEF COMPLAINT / SYMPTOMS</div>
                  <div class="text-body-2">{{ prescription.symptoms || "—" }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">OBSERVATIONS</div>
                  <div class="text-body-2">{{ observations }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">DIAGNOSIS</div>
                  <div class="text-body-2">{{ prescription.diagnosis || "—" }}</div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <!-- Medicines -->
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-icon color="orange darken-2" class="mr-2">mdi-pill</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Medicines</span>
                <v-chip x-small outlined color="orange" class="ml-2">{{ medicines.length }}</v-chip>
              </div>
              <v-simple-table dense>
                <template #default>
                  <thead>
                    <tr>
                      <th>Medicine</th>
                      <th>Strength</th>
                      <th>Dosage</th>
                      <th>Frequency</th>
                      <th>Duration</th>
                      <th>Instructions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!medicines.length">
                      <td colspan="6" class="text-center grey--text py-4">No medicines added</td>
                    </tr>
                    <tr v-for="med in medicines" :key="med.id">
                      <td>{{ med.medicineName }}</td>
                      <td>{{ med.strength || "—" }}</td>
                      <td>{{ med.dosage || "—" }}</td>
                      <td>{{ med.frequency || "—" }}</td>
                      <td>{{ med.duration || "—" }}</td>
                      <td>{{ med.notes || "—" }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </v-col>

          <!-- Tips / Lifestyle Advice -->
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-icon color="teal darken-1" class="mr-2">mdi-lightbulb-on-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Tips / Lifestyle Advice</span>
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">GENERAL TIPS</div>
                  <div class="text-body-2">{{ prescription.tips || "—" }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">DIET TO FOLLOW</div>
                  <div class="text-body-2">{{ prescription.diet_to_follow || "—" }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">DIET TO AVOID</div>
                  <div class="text-body-2">{{ prescription.diet_to_avoid || "—" }}</div>
                </v-col>
              </v-row>
              <v-chip x-small class="mt-3" :color="prescription.is_yoga_advice ? 'success' : 'grey'" :dark="!!prescription.is_yoga_advice">
                <v-icon x-small left>mdi-yoga</v-icon>
                Yoga Advice: {{ prescription.is_yoga_advice ? "Yes" : "No" }}
              </v-chip>
            </v-card>
          </v-col>

          <!-- Attachments -->
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-icon color="blue-grey" class="mr-2">mdi-paperclip</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Attachments</span>
              </div>
              <v-btn small color="indigo" dark :loading="isDownloading" @click="downloadPdf">
                <v-icon left small>mdi-file-pdf-box</v-icon>
                Prescription PDF
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <v-row v-else>
        <v-col cols="12" class="text-center py-12">
          <v-icon size="80" color="grey lighten-3">mdi-prescription</v-icon>
          <h3 class="text-h6 grey--text mt-4">Prescription not found</h3>
        </v-col>
      </v-row>

      <!-- Delete Confirmation -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>
          <v-card-text class="pt-4 text-center">
            <v-icon size="64" color="error" class="mb-3">mdi-delete-alert</v-icon>
            <h3 class="mb-3">Are you sure you want to delete this prescription?</h3>
            <p class="text-body-2 grey--text">You can restore it later from the deleted prescriptions section.</p>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="showDeleteDialog = false" :disabled="isDeleting">Cancel</v-btn>
            <v-btn color="error" @click="confirmDelete" :loading="isDeleting">
              <v-icon left>mdi-delete</v-icon>
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { mapGetters } from "vuex";

export default {
  name: "PrescriptionDetailPage",
  data() {
    return {
      prescription: null,
      isLoading: false,
      isDownloading: false,
      isDeleting: false,
      showDeleteDialog: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["isSuperAdmin", "isAdmin"]),
    canDeletePrescription() {
      return this.isSuperAdmin || this.isAdmin;
    },
    appointmentId() {
      return this.prescription?.consultation?.appointment_id || null;
    },
    consultationNo() {
      return this.prescription?.consultation?.invoice_no || null;
    },
    appointmentOrderNo() {
      return this.prescription?.consultation?.appointment?.order_number || null;
    },
    prescriptionSource() {
      if (this.appointmentId) {
        return { label: "Via Appointment", color: "teal", icon: "mdi-calendar-check" };
      }
      if (this.prescription?.consultation_id) {
        return { label: "Direct Consultation", color: "blue", icon: "mdi-video" };
      }
      return { label: "Independent (No Consultation)", color: "grey", icon: "mdi-file-document-outline" };
    },
    observations() {
      return this.prescription?.consultation?.doctor_notes || "—";
    },
    doctorSpecialties() {
      const specialities = this.prescription?.doctor?.doctor_specialities || [];
      return specialities.map((s) => s.specialization?.description).filter(Boolean).join(", ");
    },
    doctorRegistrationNumber() {
      const certificates = this.prescription?.doctor?.council_registration_certificates || [];
      const cert = certificates[0];
      return cert?.registration_number || cert?.council_name || null;
    },
    patientAge() {
      const dob = this.prescription?.patient?.dob;
      if (!dob) return null;
      const diff = Date.now() - new Date(dob).getTime();
      return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
    },
    medicines() {
      return (this.prescription?.prescription_items || []).map((item) => {
        const variant = item.product_variant || item.seller_product_variant?.product_variant;
        const productName = variant?.product?.name || "";
        const variantName = variant?.name || "";
        return {
          id: item.id,
          medicineName: [productName, variantName].filter(Boolean).join(" — ") || "Unnamed medicine",
          strength: variant?.potency || null,
          dosage: item.dosage,
          frequency: item.frequency,
          duration: item.duration,
          notes: item.notes,
        };
      });
    },
  },
  mounted() {
    this.fetchPrescription();
  },
  methods: {
    async fetchPrescription() {
      this.isLoading = true;
      try {
        const response = await axios.get(`/prescriptions/${this.$route.params.id}?context=panel`);
        this.prescription = response.data.success ? response.data.data : null;
      } catch (error) {
        console.error("Failed to load prescription:", error);
        this.prescription = null;
      }
      this.isLoading = false;
    },

    async downloadPdf() {
      this.isDownloading = true;
      try {
        const response = await axios.get(`/prescriptions/${this.$route.params.id}/pdf`, {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `prescription_${this.prescription?.id || ""}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.error("Failed to download prescription PDF");
      }
      this.isDownloading = false;
    },

    async confirmDelete() {
      this.isDeleting = true;
      try {
        await axios.post(`/prescriptions/delete/${this.$route.params.id}`);
        this.$toast.success("Prescription deleted successfully.");
        this.$router.push("/prescriptions");
      } catch (error) {
        this.$toast.error("Failed to delete prescription");
      }
      this.isDeleting = false;
      this.showDeleteDialog = false;
    },

    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN");
    },

    formatDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      return `${d.toLocaleDateString("en-IN")} ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
    },
  },
};
</script>

<style scoped>
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}

.info-value {
  font-size: 0.85rem;
  font-weight: 500;
  text-align: right;
}
</style>
